import React, { useState } from 'react';
import { Button, Form, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { patch } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';

// Visual tab module: expects full website object
function Visual({ website, onUpdated }) {
  const hasWebsite = !!website;

  // Popular Google Fonts grouped by style/category
  const fontGroups = {
    'Sans Serif': [
      'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Nunito', 'Noto Sans', 'PT Sans', 'Rubik',
      'Work Sans', 'Ubuntu', 'Roboto Condensed', 'Quicksand', 'Fira Sans', 'Karla', 'Mulish', 'Heebo', 'DM Sans',
      'Titillium Web', 'Cabin', 'Manrope', 'Source Sans 3', 'IBM Plex Sans', 'Raleway'
    ],
    'Serif': [
      'Merriweather', 'Playfair Display', 'Lora', 'PT Serif', 'Roboto Slab', 'Bitter', 'Cardo', 'Source Serif 4', 'Noto Serif', 'Cinzel'
    ],
    'Display': [
      'Oswald', 'Bebas Neue', 'Abril Fatface', 'Anton', 'Bangers', 'Josefin Sans'
    ],
    'Handwriting': [
      'Pacifico', 'Dancing Script', 'Satisfy', 'Caveat', 'Indie Flower', 'Amatic SC', 'Lobster'
    ],
    'Monospace': [
      'Inconsolata', 'Source Code Pro', 'Fira Code', 'JetBrains Mono', 'Ubuntu Mono', 'Anonymous Pro'
    ]
  };

  const allFonts = Object.values(fontGroups).flat();

  const defaults = {
    backgroundColor: '#fff',
    textColor: '#222',
    textGlow: false,
    font: 'Quicksand',
    normalFontWeight: 400,
    boldFontWeight: 700
  };

  const initial = {
    ...defaults,
    ...((website && (website.visualSettings || website.visual_settings)) || {})
  };

  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(initial);

  const startEdit = () => {
    setForm({
      ...defaults,
      ...(website.visualSettings || website.visual_settings || {})
    });
    setError('');
    setEdit(true);
  };

  const cancelEdit = () => {
    setError('');
    setEdit(false);
  };

  const isHexColor = (val) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((val || '').trim());
  const isValidWeight = (val) => {
    const n = Number(val);
    return Number.isInteger(n) && n >= 100 && n <= 900 && n % 100 === 0;
  };

  const save = async (e) => {
    e.preventDefault();
    // Basic validation: colors valid and font non-empty
    if (!isHexColor(form.backgroundColor) || !isHexColor(form.textColor)) {
      toast.error('Please provide valid hex colors, e.g. #ffffff or #222');
      return;
    }
    if (!allFonts.includes(form.font)) {
      toast.error('Font must be selected from the provided Google fonts list.');
      return;
    }
    if (!isValidWeight(form.normalFontWeight) || !isValidWeight(form.boldFontWeight)) {
      toast.error('Font weights must be 100–900 in steps of 100 (e.g., 400 for normal, 700 for bold).');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = { visualSettings: {
        backgroundColor: form.backgroundColor,
        textColor: form.textColor,
        textGlow: !!form.textGlow,
        font: form.font,
        normalFontWeight: Number(form.normalFontWeight),
        boldFontWeight: Number(form.boldFontWeight)
      }};
      const resp = await patch(`websites/${website.id}/visual-settings`, payload);
      const updated = resp?.website || resp || { ...website, visualSettings: payload.visualSettings };
      onUpdated?.(updated);
      setEdit(false);
    } catch (e) {
      toast.error('Failed to save visual settings.');
    } finally {
      setSaving(false);
    }
  };

  const ColorInput = ({ label, value, onChange, controlId }) => (
    <Form.Group className="mb-3 form-row-horizontal" controlId={controlId}>
      <Form.Label>{label}</Form.Label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          title={label}
          style={{ width: 42, height: 32, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}
        />
        <Form.Control
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#ffffff"
        />
      </div>
    </Form.Group>
  );

  return (
    <div>
      {!hasWebsite ? (
        <Alert variant="warning">Website not found.</Alert>
      ) : !edit ? (
        <>
          <p style={{ marginBottom: '0.75rem', color: '#64748b' }}>Branding, colors, and typography.</p>
          <ul style={{ marginBottom: 0 }}>
            <li><strong>Background color:</strong> <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span style={{ display: 'inline-block', width: 16, height: 16, background: form.backgroundColor, borderRadius: 4, border: '1px solid #cbd5e1' }} /> {form.backgroundColor}</span></li>
            <li><strong>Text color:</strong> <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span style={{ display: 'inline-block', width: 16, height: 16, background: form.textColor, borderRadius: 4, border: '1px solid #cbd5e1' }} /> {form.textColor}</span></li>
            <li><strong>Text glow:</strong> {form.textGlow ? 'Enabled' : 'Disabled'}</li>
            <li><strong>Font:</strong> <span style={{ fontFamily: form.font }}>{form.font}</span></li>
            <li><strong>Font weight (normal):</strong> {form.normalFontWeight}</li>
            <li><strong>Font weight (bold):</strong> {form.boldFontWeight}</li>
          </ul>
          <div style={{ marginTop: '12px' }}>
            <Button className="login-button" size="sm" onClick={startEdit}>Edit</Button>
          </div>
        </>
      ) : (
        <>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={save}>
            <ColorInput label="Background color" value={form.backgroundColor} onChange={(v) => setForm({ ...form, backgroundColor: v })} controlId="vsBg" />
            <ColorInput label="Text color" value={form.textColor} onChange={(v) => setForm({ ...form, textColor: v })} controlId="vsText" />

            <Form.Group className="mb-3 form-row-horizontal" controlId="vsGlow">
              <Form.Label>Text glow</Form.Label>
              <Form.Check
                type="checkbox"
                checked={!!form.textGlow}
                onChange={(e) => setForm({ ...form, textGlow: e.target.checked })}
                label={form.textGlow ? 'Enabled' : 'Disabled'}
              />
            </Form.Group>

            <Form.Group className="mb-3 form-row-horizontal" controlId="vsFont">
              <Form.Label>Font</Form.Label>
              <Form.Select
                value={form.font}
                onChange={(e) => setForm({ ...form, font: e.target.value })}
              >
                {Object.entries(fontGroups).map(([group, fonts]) => (
                  <optgroup key={group} label={group}>
                    {fonts.map((f) => (
                      <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                    ))}
                  </optgroup>
                ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col>
                <Form.Group className="mb-3 form-row-horizontal" controlId="vsWeightNormal">
                  <Form.Label>Font weight (normal)</Form.Label>
                  <Form.Control
                    type="number"
                    min={100}
                    max={900}
                    step={100}
                    value={form.normalFontWeight}
                    onChange={(e) => setForm({ ...form, normalFontWeight: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3 form-row-horizontal" controlId="vsWeightBold">
                  <Form.Label>Font weight (bold)</Form.Label>
                  <Form.Control
                    type="number"
                    min={100}
                    max={900}
                    step={100}
                    value={form.boldFontWeight}
                    onChange={(e) => setForm({ ...form, boldFontWeight: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="submit" className="primary-button" disabled={saving}>
                {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
              </Button>
              <Button className='primary-button' onClick={cancelEdit} disabled={saving}>Cancel</Button>
            </div>
          </Form>
        </>
      )}
    </div>
  );
}

export default Visual;
